import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Pagination,
  PaginationItem,
  Typography,
} from "@mui/material";
import {
  formatPrice,
  discountedEventPrice,
  hasPrice,
} from "../../helpers/currency";
import moment from "moment";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "../../components/Loader";
import Background from "../../components/Background";
import { categoryList } from "../../constants/categories";
import { NextSlot, PreviousSlot } from "./IndustryHub";
import { formatEventType } from "../../constants/RegionalLocationList";
import { getTimezoneLabel } from "../../helpers/constants";
import useDebounce from "../../hooks/useDebounce";
import useDashboardList from "../../hooks/useDashboardList";
import { CloseOutlined } from "@mui/icons-material";
import ConnectButtonModal from "../../components/ConnectButtonModal";

function showButton(evt) {
  return evt.eventType === "webinar" || evt.eventType === "virtual-event";
}
export default function Marketplace({ searchTerm }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const isPremium = useSelector(
    (state) => state.profileSlice?.profile?.account?.isPremiumUser,
  );
  const [selectedCategory, setSelectedCategory] = useState("");
  const [page, setPage] = useState(1);
  const urlParams = new URLSearchParams(location.search);
  const corporateId = urlParams.get("corporateId");
  const debouncedSearchCategoryText = useDebounce(searchTerm, 500);

  const paginationSlots = {
    previous: PreviousSlot,
    next: NextSlot,
  };

  const request = useMemo(
    () => ({
      url: `/v1/web/event/list/all?page=${page}&limit=12`,
      method: "POST",
      body: {
        eventType: selectedCategory,
        eventTitle: debouncedSearchCategoryText,
        createdBy: corporateId,
      },
    }),
    [page, selectedCategory, debouncedSearchCategoryText, corporateId],
  );
  const { data, loading } = useDashboardList(request, token);
  const eventData = data.results;
  const totalRecords = data.total;

  const filterOutEventList = (type) => {
    setSelectedCategory(type);
    setPage(1);
  };
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePreviousClick = () => {
    if (page > 1) {
      setPage((page) => page - 1);
    }
  };

  const handleNextClick = () => {
    setPage((page) => page + 1);
  };

  const handleClear = () => {
    navigate("/dashboard?active=learn");
  };
  return (
    <>
      <Background dashboardChrome={true}>
        {loading && <Loader />}
        <Box className="dashboard-main-div">
          <Box
            sx={{
              padding: {
                xs: "10px 0 20px",
                sm: "10px 0 20px",
                md: "10px 0 20px",
                lg: "10px 0 20px",
                xl: "20px 0 30px",
              },
            }}
          >
            <Container maxWidth="xl">
              <Grid container spacing={{ lg: 3, sm: 2, xs: 1 }}>
                <Grid
                  item
                  xs={12}
                  lg={10}
                  xl={9}
                  sx={{
                    margin: "auto",
                    overflow: "auto",
                  }}
                >
                  <Box
                    sx={{
                      width: {
                        xs: "535px",
                        sm: "auto",
                      },
                      overflowX: "auto",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: {
                        xs: "5px",
                        sm: "10px",
                        md: "10px",
                        lg: 3,
                      }, // Optional spacing between elements
                      scrollbarWidth: "none", // For Firefox
                      msOverflowStyle: "none", // For Internet Explorer and Edge
                      "&::-webkit-scrollbar": {
                        display: "none", // For Chrome, Safari, and other WebKit browsers
                      },
                    }}
                  >
                    {categoryList?.map((category) => {
                      return (
                        <Box key={category.key}>
                          <Button
                            onClick={() => filterOutEventList(category.key)}
                            sx={{
                              backgroundColor:
                                selectedCategory === category.key
                                  ? "#2A4876"
                                  : "#7BB9D5",

                              borderRadius: "100px",
                              padding: {
                                xs: "10px 15px",
                                sm: "10px 20px",
                                md: "10px 20px",
                                lg: "10px 30px",
                              },
                              color:
                                selectedCategory === category.key
                                  ? "#ffffff"
                                  : "#152134",
                              fontSize: {
                                xs: "12px",
                                sm: "14px",
                                md: "16px",
                                lg: "17px",
                                xl: "18px",
                              },
                              fontWeight: 400,
                              textTransform: "capitalize",
                              width: "auto",
                              minWidth: "initial",
                            }}
                          >
                            {category?.value}
                          </Button>
                        </Box>
                      );
                    })}
                    {corporateId && (
                      <Typography
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        onClick={handleClear}
                      >
                        Clear <CloseOutlined sx={{ ml: 0.5 }} />
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box>
            <Container maxWidth="xl">
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={10}
                  xl={9}
                  sx={{ margin: "auto" }}
                >
                  <Grid container spacing={{ lg: 5, sm: 2, xs: 2 }}>
                    {console.log("eventData", eventData)}
                    {eventData?.length > 0 &&
                      eventData?.map((evt) => {
                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={6}
                            lg={6}
                            key={evt?.id ?? evt?._id}
                          >
                            <Box
                              onClick={() =>
                                navigate(
                                  `/dashboard/details/${evt?.id ?? evt?._id}`,
                                  {
                                    state: evt,
                                  },
                                )
                              }
                              sx={{
                                width: "100%",
                                height: "100%",
                                overflow: "hidden",
                                borderRadius: "20px",
                                position: "relative",
                                cursor: "pointer",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  width: "auto",
                                  padding: "0 10px",
                                  height: "38px",
                                  borderRadius: "50px",
                                  background: "#fff",
                                  fontSize: {
                                    xs: "10px",
                                    sm: "10px",
                                    md: "12px",
                                    lg: "12px",
                                    xl: "14px",
                                  },
                                  fontWeight: 400,
                                  color: "#2A4876",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {formatEventType("key", evt?.eventType)}
                              </Box>
                              <Box
                                sx={{
                                  background:
                                    "linear-gradient(180deg, rgba(0, 0, 0, 0) 5.83%, #000000 100%)",
                                  width: "100%",
                                  height: "100%",
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                }}
                              ></Box>
                              <Box
                                component="img"
                                src={evt?.eventPhoto}
                                alt="logo"
                                sx={{
                                  width: "100%",
                                  height: {
                                    xs: "200px",
                                    sm: "200px",
                                    md: "250px",
                                    lg: "300px",
                                    xl: "300px",
                                  },
                                  objectFit: "cover",
                                  borderRadius: "20px",
                                }}
                              />
                              <Box
                                sx={{
                                  width: "100%",
                                  position: "absolute",
                                  left: 0,
                                  bottom: 0,
                                  padding: {
                                    xs: "15px",
                                    sm: "15px",
                                    md: "15px",
                                    lg: "10px 10px 15px",
                                    xl: "20px",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    paddingBottom: "10px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: "#ffffff",
                                      fontSize: {
                                        xs: "16px",
                                        sm: "16px",
                                        md: "16px",
                                        lg: "16px",
                                        xl: "20px",
                                      },
                                      lineHeight: {
                                        xs: "20px",
                                        sm: "20px",
                                        md: "20px",
                                        lg: "20px",
                                        xl: "30px",
                                      },
                                      fontWeight: 600,
                                      textAlign: "left",
                                    }}
                                  >
                                    {evt?.eventTitle}
                                  </Typography>
                                  {hasPrice(evt) && !isPremium && (
                                    <Box
                                      sx={{
                                        padding: "5px 10px",
                                        borderRadius: "10px",
                                        background: "#ffffff",
                                      }}
                                    >
                                      {evt?.isPurchased ? (
                                        <Typography
                                          sx={{
                                            color: "#2A4876",
                                            fontSize: {
                                              xs: "12px",
                                              sm: "14px",
                                            },
                                            fontWeight: 700,
                                          }}
                                        >
                                          Purchased
                                        </Typography>
                                      ) : evt?.discountInfo?.hasDiscount ? (
                                        <Box
                                          sx={{
                                            display: "block",
                                            alignItems: "stretch",
                                            borderRadius: "10px",
                                            overflow: "hidden",
                                            boxShadow:
                                              "0px 4px 10px rgba(0,0,0,0.15)",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {/* SALE label */}
                                          <Box
                                            sx={{
                                              backgroundColor: "#2A4876",
                                              color: "#fff",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              px: { xs: 1, sm: 1.5 },
                                              py: { xs: 0.25, sm: 0.5 },
                                              fontSize: {
                                                xs: "10px",
                                                sm: "12px",
                                              },
                                              fontWeight: 700,
                                            }}
                                          >
                                            SALE
                                          </Box>

                                          {/* Price section */}
                                          <Box
                                            sx={{
                                              backgroundColor: "#fff",
                                              px: { xs: 1, sm: 1.5 },
                                              py: { xs: 0.25, sm: 0.5 },
                                              display: "flex",
                                              alignItems: "center",
                                              gap: { xs: 0.5, sm: 1 },
                                            }}
                                          >
                                            {/* Original price */}
                                            <Typography
                                              sx={{
                                                color: "#8A8A8A",
                                                fontSize: {
                                                  xs: "10px",
                                                  sm: "12px",
                                                },
                                                fontWeight: 500,
                                                textDecoration: "line-through",
                                              }}
                                            >
                                              ${formatPrice(evt?.eventFee)}
                                            </Typography>

                                            {/* Discounted price */}
                                            <Typography
                                              sx={{
                                                color: "#2A4876",
                                                fontSize: {
                                                  xs: "12px",
                                                  sm: "14px",
                                                },
                                                fontWeight: 700,
                                              }}
                                            >
                                              $
                                              {formatPrice(
                                                discountedEventPrice(evt),
                                              )}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      ) : (
                                        <Typography
                                          sx={{
                                            color: "#2A4876",
                                            fontSize: {
                                              xs: "12px",
                                              sm: "14px",
                                            },
                                            fontWeight: 700,
                                          }}
                                        >
                                          ${formatPrice(evt?.eventFee)}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                                {showButton(evt) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      paddingBottom: "10px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src="/images/icons/time-circle-icon.png"
                                        alt="logo"
                                        sx={{
                                          width: {
                                            xs: "18px",
                                            sm: "18px",
                                            md: "18px",
                                            lg: "12px",
                                            xl: "18px",
                                          },
                                          height: {
                                            xs: "18px",
                                            sm: "18px",
                                            md: "18px",
                                            lg: "12px",
                                            xl: "18px",
                                          },
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          color: "#ffffff",
                                          fontSize: {
                                            xs: "12px",
                                            sm: "12px",
                                            md: "12px",
                                            lg: "12px",
                                            xl: "16px",
                                          },
                                          fontWeight: 400,
                                          paddingLeft: {
                                            xs: "10px",
                                            sm: "10px",
                                            md: "10px",
                                            lg: "5px",
                                            xl: "10px",
                                          },
                                          textAlign: "left",
                                        }}
                                      >
                                        {evt?.eventTimeStart}-
                                        {evt?.eventTimeEnd}{" "}
                                        {getTimezoneLabel(evt?.timezone)}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        paddingLeft: {
                                          xs: "10px",
                                          sm: "10px",
                                          md: "10px",
                                          lg: "15px",
                                          xl: "20px",
                                        },
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src="/images/icons/calendar-icon-white.png"
                                        alt="logo"
                                        sx={{
                                          width: {
                                            xs: "18px",
                                            sm: "18px",
                                            md: "18px",
                                            lg: "12px",
                                            xl: "18px",
                                          },
                                          height: {
                                            xs: "18px",
                                            sm: "18px",
                                            md: "18px",
                                            lg: "12px",
                                            xl: "18px",
                                          },
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          color: "#ffffff",
                                          fontSize: {
                                            xs: "12px",
                                            sm: "12px",
                                            md: "12px",
                                            lg: "12px",
                                            xl: "16px",
                                          },
                                          fontWeight: 400,
                                          paddingLeft: {
                                            xs: "10px",
                                            sm: "10px",
                                            md: "10px",
                                            lg: "5px",
                                            xl: "10px",
                                          },
                                          textAlign: "left",
                                        }}
                                      >
                                        {moment
                                          .utc(evt?.eventDateStart)
                                          .format("MMM DD, YYYY")}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {evt?.createdBy?.sponsorProfile.logo && (
                                    <Box
                                      component="img"
                                      src={evt?.createdBy?.sponsorProfile.logo}
                                      alt="logo"
                                      sx={{
                                        width: "35px",
                                        height: "35px",
                                        objectFit: "cover",
                                        borderRadius: "50px",
                                      }}
                                    />
                                  )}
                                  {evt?.createdBy?.sponsorProfile
                                    ?.companyName && (
                                    <Typography
                                      sx={{
                                        color: "#ffffff",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        paddingLeft: "10px",
                                      }}
                                    >
                                      {
                                        evt?.createdBy?.sponsorProfile
                                          ?.companyName
                                      }
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                  </Grid>
                  {totalRecords > 12 && (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: {
                          xs: "15px 0",
                          sm: "30px",
                          md: "30px",
                          lg: "40px 40px 0",
                        },
                        marginBottom: "60px",
                        "& ul li:last-child": {
                          position: {
                            xs: "unset",
                            md: "absolute",
                          },
                          right: "10%",
                        },
                        "& ul li:first-child": {
                          position: {
                            xs: "unset",
                            md: "absolute",
                          },
                          left: "10%",
                        },
                      }}
                    >
                      <Pagination
                        count={Math.ceil(totalRecords / 12)}
                        page={page}
                        onChange={handlePageChange}
                        renderItem={(item) => (
                          <PaginationItem
                            sx={{
                              fontSize: {
                                xs: "12px",
                                sm: "14px",
                                md: "16px",
                              },
                              minWidth: {
                                xs: "28px",
                                sm: "32px",
                              },
                              height: {
                                xs: "28px",
                                sm: "32px",
                              },
                              margin: {
                                xs: 0,
                                sm: "0 3px",
                              },
                            }}
                            {...item}
                            slots={paginationSlots}
                            slotProps={{
                              previous: { onClick: handlePreviousClick },
                              next: {
                                onClick: handleNextClick,
                                disabled: page === Math.ceil(totalRecords / 12),
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Box>
        <ConnectButtonModal />
      </Background>
    </>
  );
}
